import { useEffect, useMemo, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { useMessageManager } from '@framework/message';
import {
  IDictionariesState,
  initDictionaries,
  prepareDictionariesData,
  filterSupportedProducts,
} from '@/creditflowresearch/application/Dictionaries/helpers';
import createDictionaryAccessors from '@/creditflowresearch/application/Dictionaries/createDictionaryAccessors';
import IDictionaries from '@/creditflowresearch/domain/vo/dictionaries/IDictionaries';
import DictionaryRepository from '@/creditflowresearch/infrastructure/repository/DictionaryRepository';
import {
  transformSectors,
  transformIndustries,
  transformDocumentTypeGroups,
} from '@/creditflowresearch/application/Dictionaries/serverSide/legacyMappers';
import RatingAgenciesMappingService from '@/creditflowresearch/domain/RatingAgenciesMapping';

const useDictionaries = (serverDictionaries: IDictionariesState) => {
  const container = getContainer();
  const { getErrorMessage } = useMessageManager();
  const [state, setState] = useState<IDictionariesState>(serverDictionaries || initDictionaries);

  const accessors = useMemo(() => createDictionaryAccessors({
    industriesListAll: state.industriesListAll,
    dealStatuses: state.dealStatuses,
    currencies: state.currencies,
    rankings: state.rankings,
    timeZones: state.timeZones,
    countries: state.countries,
    sizeDescriptions: state.sizeDescriptions,
    ratingAgencies: state.ratingAgencies,
  }), [
    state.industriesListAll,
    state.dealStatuses,
    state.currencies,
    state.rankings,
    state.timeZones,
    state.countries,
    state.sizeDescriptions,
    state.ratingAgencies,
  ]);

  const fetchFEDictionaries = async () => {
    const dictionaryRepository = container.get<DictionaryRepository>(DictionaryRepository);
    try {
      const [
        sectors,
        industries,
        documentTypeGroups,
        headerMenuItems,
        products,
        regions,
        ratingAgencies,
      ] = await Promise.all([
        dictionaryRepository.getSectors(),
        dictionaryRepository.getIndustries(),
        dictionaryRepository.getDocumentTypeGroups(),
        dictionaryRepository.getHeaderMenuItems(),
        dictionaryRepository.getProducts(),
        dictionaryRepository.getRegions(),
        dictionaryRepository.getRatingAgencies(),
      ]);

      const coreDictionaries: IDictionaries = {
        headerMapping: headerMenuItems?.headerMapping ?? {},
        toolsList: headerMenuItems?.toolsList ?? [],
        productsList: filterSupportedProducts(products ?? []),
        regionsList: regions ?? [],
        sectorsList: transformSectors(sectors ?? []),
        industriesList: transformIndustries(industries ?? []),
        ratingAgenciesMap: RatingAgenciesMappingService.buildMap(ratingAgencies ?? []),
        researchDocumentGroupList: transformDocumentTypeGroups(documentTypeGroups ?? []),
      };
      setState(prepareDictionariesData(coreDictionaries));
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    }
  };

  useEffect(() => {
    if (state.isHeaderInitialized) {
      return;
    }
    if (!serverDictionaries || serverDictionaries.hasServerError) {
      fetchFEDictionaries();
    }
  }, []);

  return {
    ...state,
    ...accessors,
  };
};

export default useDictionaries;
